import { useRouteContext } from "@tanstack/react-router";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { arrayAsMap } from "@/utils/util";
import { DropDownModel } from "@/models.ts";
import { fetchAuthenticatedData } from "@/queries/shared";
import { recordTypeLabels } from "@/constants/lookups.ts";
import { type RecordType } from "@/models.ts";

import { IconName } from "@/components/shared/Icon";

export enum FileStatusEnum {
  Created = 0,
  Staged,
  Staging,
  Validated, // not in use
  Validating, // not in use
  Posted,
  Posting,
  Standardizing,
  Standardized,
  Failed,
  Rejected,
  // Archived // not in use
}

export interface FileStatusModel extends DropDownModel {
  TextColor?: "success" | "danger" | "secondary" | undefined;
  Icon?: IconName | undefined;
}

export const FileStatus = arrayAsMap(
  [
    { Text: "Created", Value: "0", TextColor: "secondary", Icon: "clock" },
    { Text: "Staged", Value: "1", TextColor: "success", Icon: "success" },
    { Text: "Staging", Value: "2", TextColor: "secondary", Icon: "clock" },
    // { Text: "Validated", Value: "3", TextColor: 'success', Icon: 'success' },
    // { Text: "Validating", Value: "4", TextColor: 'secondary', Icon: 'clock' },
    { Text: "Posted", Value: "5", TextColor: "success", Icon: "success" },
    { Text: "Posting", Value: "6", TextColor: "secondary", Icon: "clock" },
    {
      Text: "Standardizing",
      Value: "7",
      TextColor: "secondary",
      Icon: "clock",
    },
    { Text: "Standardized", Value: "8", TextColor: "success", Icon: "success" },
    { Text: "Failed", Value: "9", TextColor: "danger", Icon: "error" },
    { Text: "Rejected", Value: "10", TextColor: "danger", Icon: "error" },
    // { Text: "Archived", Value: "11", TextColor: 'secondary' },
  ] as FileStatusModel[],
  (x) => x.Value
);

export type FileSubmission = {
  Id: string;
  CreateDate: string;
  CreatedByUserId: string;
  ReportingLea: string;
  Esiid: string;
  ClientFileName: string;
  CurrentStatus: FileStatusEnum;
  StatusDate: string;
  RecordTypeCode: RecordType;
  // RecordCount: number,
  InputRecords: number;
  Errors: number;
  ValidRecords: number;
};
export type FileSubmissionUI = FileSubmission & {
  StatusName: string;
};

// Although fieldname should be a string, we use a type parameters for better typing in statically-known record type contexts.
export type BaseValidationError<T> = {
  Id: string;
  Type: string;
  Fieldname: T;
  Code: string;
  ErrorRecordId: number;
  RecordType: RecordType;
  LocalRecordId: string;
  Record: string;
  Message: string;
};

export type ValidationError<T> = BaseValidationError<T> & {
  FileSubmissionId: number;
};

export type CandidateValidationError<T> = BaseValidationError<T> & {
  CandidateSubmissionId: number;
};

export type FileSubmissionState = {
  currentStatus: FileStatusEnum | undefined;
  files: FileSubmission[] | undefined;
  errors: ValidationError<string>[] | undefined;
};

export type FileUploadResponse = {
  filesubmission: FileSubmission;
  errors: ValidationError<string>[];
};

export type Submission = {
  Id: string;
  SubmissionCode: string;
  FiscalYear: number;
  Status: string;
  Description: string;
  DefaultWindowStart: string;
  DefaultDeadline: string;
  DefaultAmendmentEnd: string;
};

export type Anomaly = {
  Id: string;
  AnomalyProcessRunId: number;
  AnomalyProcessId: number;
  AnomalyRunId: number;
  ReferenceId: number;
  ReferenceRecordType: RecordType;
  ReferenceRecordId: string;
  ReferenceRecord: string;
  PreschoolCode: string;
  CSPPID: string;
  LocalFamilyId: string;
  LocalStaffId: string;
  LocalClassroomId: string;
  ErrorCode: string;
  AcademicYearId: string;
  DiscrepancyRecordType: RecordType;
  DiscrepancyRecordId: string;
  DiscrepancyId: number;
  DiscrepancyReportingLea: string;
};

export type AnomalyRun = {
  Id: string;
  FilesubmissionId: number;
  StartTime: string;
  EndTime: string;
  ReportingLea: string;
  Detected: number;
  Anomalies: Anomaly[];
};

export type AnomalyOverride = {
  Id: number;
  Anomaly: Anomaly;
  CertificationId: number;
  Status: string;
  Notes: string;
  LastModified: string;
  LastModifiedDate: string;
};

export type AnomalyProcess = {
  Id: number;
  Name: string;
  Description: string;
  Active: boolean;
  Type: string;
  DeactivatedOn: string | null;
  DeactivatedBy: string | null;
  DeactivationReason: string | null;
};

export type Certification = {
  Id: number;
  ReportingLea: string;
  ReportingCalendarId: number;
  Esiid: number;
  Submission: Submission;
  AnomalyRun: AnomalyRun | null;
  AnomalyRunId: number | null;
  Status: string;
  Step: string;
  LastReviewer: string;
  LastReviewerDate: string;
  WindowStart: string;
  WindowEnd: string;
  Deadline: string;
  AmendmentEnd: string;
  AnomalyOverrides: AnomalyOverride[];
  CertifierFirstName: string | null;
  CertifierLastName: string | null;
  CertifierEmail: string | null;
  CertifierPhoneNumber: string | null;
  CertifierExtension: string | null;
};

export type CertificationRunStats = {
  CertificationId: number;
  AnomalyRunId: number;
  Errors: number;
  Warnings: number;
  TotalCHLDRecords: number;
  TotalSTFFRecords: number;
  TotalCLRMRecords: number;
  Certification: {
    Id: number;
    ReportingCalendarId: number;
    Status: string;
    WindowStart: string;
    Deadline: string;
    Esiid: number;
    LastReviewerDate: string | null;
    CertifierFirstName: string | null;
    CertifierLastName: string | null;
  } | null;
  AnomalyRun: {
    Id: number;
    ReportingHistoryId: number;
    StartTime: string;
    EndTime: string;
    Detected: number;
    Esiid: number;
  } | null;
};

export type CertificationLog = {
  Id: number;
  CertificationId: number;
  Certification: Certification | null;
  LockAnomalyRunId: number | null;
  UnLockAnomalyRunId: number | null;
  ReviewedReportId: number | null;
  Reviewer: string | null;
  ReviewerDate: string | null;
  StatusUpdate: string | null;
};

export type CertificationStats = CertificationRunStats[];

export type AnomalyRunStats = CertificationRunStats;

export type CertificationCycleStatusRow = {
  ReportingCalendarId: string;
  ESIID: number;
  LEAName: string;
  VendorNumber: string;
  Status: string;
  CertifierFirstName: string | null;
  CertifierLastName: string | null;
  CertifierEmail: string | null;
  CertifierPhoneNumber: string | null;
  CertifierExtension: string | null;
  TotalEnrollment: number;
};

export function certificationMutationFunctions(
  leaId: string,
  handleCloseDialog: () => void
) {
  const leaContext = useRouteContext({ from: "/$leaid" });
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: ({ urlVal, cert }: { urlVal: string; cert: Certification }) => {
      return fetchAuthenticatedData<Certification>(leaContext, urlVal, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify(cert),
      });
    },
    onSuccess: (resp) => {
      if (resp) {
        queryClient.resetQueries({
          queryKey: ["certifications", leaId, resp.Id.toString()],
        });
        handleCloseDialog();
        return;
      }
    },
    onError: (error) => {
      console.error(error);
    },
  });
  return {
    mutationState: mutation,
    handleSave: async function (cert: Certification) {
      var urlVal = `/json/certification/${leaId}/edit`;
      mutation.mutate({ urlVal, cert });
    },
  };
}

export function anomalyOverrideMutationFunctions(
  leaId: string,
  handleCloseDialog: () => void
) {
  const leaContext = useRouteContext({ from: "/$leaid" });
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: ({
      urlVal,
      override,
    }: {
      urlVal: string;
      override: AnomalyOverride;
    }) => {
      return fetchAuthenticatedData<AnomalyOverride>(leaContext, urlVal, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify(override),
      });
    },
    onSuccess: (resp) => {
      if (resp) {
        queryClient.resetQueries({
          queryKey: ["certifications", leaId, resp.CertificationId.toString()],
        });
        handleCloseDialog();
        return;
      }
    },
    onError: (error) => {
      console.error(error);
    },
  });
  return {
    mutationState: mutation,
    handleSave: async function (override: AnomalyOverride) {
      var urlVal = `/json/anomaly/${leaId}/edit_override`;
      mutation.mutate({ urlVal, override });
    },
  };
}

export function getRecordTypeLabel(
  recordType: RecordType
): string | RecordType {
  return recordTypeLabels[recordType] || recordType;
}
